import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { Wishlist } from '@theme/wishlist';
import { formatMoney } from '@theme/money-formatting';

/**
 * Renders the current wishlist as a product grid. The product list isn't known until
 * `Wishlist` resolves it client-side (guest: localStorage, signed in: the wishlist
 * service), so this fetches each product from the public `/products/{handle}.js`
 * endpoint once it does. A product that fails to load (deleted, unpublished, or renamed
 * since it was wishlisted) is silently skipped rather than breaking the whole grid.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} grid
 *
 * @extends {Component<Refs>}
 */
class WishlistPage extends Component {
  requiredRefs = ['grid'];

  /** @type {AbortController | null} */
  #inFlight = null;

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.wishlistUpdate, this.#onWishlistUpdate);
    this.#refresh();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.wishlistUpdate, this.#onWishlistUpdate);
    this.#inFlight?.abort();
  }

  #onWishlistUpdate = () => {
    this.#refresh();
  };

  async #refresh() {
    const ids = Wishlist.getAll();

    if (ids.length === 0) {
      this.#clearCards();
      // Don't flash "empty" before the initial load (account fetch/merge) has actually
      // resolved - wait for the real wishlist:update event once it has.
      this.setAttribute('state', Wishlist.hasLoaded ? 'empty' : 'loading');
      return;
    }

    this.#inFlight?.abort();
    const controller = new AbortController();
    this.#inFlight = controller;

    this.setAttribute('state', 'loading');

    const results = await Promise.allSettled(ids.map((handle) => this.#fetchProduct(handle, controller.signal)));

    if (controller.signal.aborted) return;

    const products = results
      .filter((result) => result.status === 'fulfilled' && result.value)
      .map((result) => /** @type {any} */ (result).value);

    this.#clearCards();

    if (products.length === 0) {
      this.setAttribute('state', 'error');
      return;
    }

    this.setAttribute('state', 'ready');
    for (const product of products) {
      this.refs.grid.insertAdjacentHTML('beforeend', this.#cardMarkup(product));
    }
  }

  /**
   * @param {string} handle
   * @param {AbortSignal} signal
   */
  async #fetchProduct(handle, signal) {
    try {
      const response = await fetch(`/products/${encodeURIComponent(handle)}.js`, {
        headers: { Accept: 'application/json' },
        signal,
      });

      if (!response.ok) return null;
      return await response.json();
    } catch (_error) {
      return null;
    }
  }

  #clearCards() {
    for (const card of this.refs.grid.querySelectorAll('[data-wishlist-card]')) {
      card.remove();
    }
  }

  /** @param {any} product */
  #cardMarkup(product) {
    const image = product.featured_image ?? product.images?.[0];
    const format = this.dataset.moneyFormat || '${{amount}}';
    const currency = this.dataset.currency || 'USD';
    const price = formatMoney(product.price, format, currency);
    const compareAtPrice =
      product.compare_at_price && product.compare_at_price > product.price
        ? formatMoney(product.compare_at_price, format, currency)
        : null;

    return `
      <a href="${product.url}" class="pravaa-card" data-wishlist-card>
        <div class="pravaa-media">
          ${image ? `<img src="${image}" alt="${escapeHtml(product.title)}" loading="lazy">` : ''}
          ${product.available === false ? '<span class="pravaa-badge">Sold Out</span>' : ''}
          <wishlist-button class="pravaa-wishlist" product-id="${escapeHtml(product.handle)}">
            <button
              type="button"
              class="wishlist-button__toggle"
              ref="toggle"
              aria-pressed="true"
              aria-label="Remove from wishlist"
              on:click="/toggle"
            >
              <span class="svg-wrapper wishlist-button__icon" aria-hidden="true">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6">
                  <path d="M12 21s-7-4.35-9.5-8.5C.5 8.5 3 5 6.5 5c2 0 3.5 1.2 4.5 3 1-1.8 2.5-3 4.5-3 3.5 0 6 3.5 4 7.5C19 16.65 12 21 12 21z" />
                </svg>
              </span>
            </button>
          </wishlist-button>
        </div>
        <div class="pravaa-info">
          <div class="pravaa-name">${escapeHtml(product.title)}</div>
          <div>
            ${compareAtPrice ? `<span class="pravaa-price old">${compareAtPrice}</span>` : ''}
            <span class="pravaa-price">${price}</span>
          </div>
        </div>
      </a>
    `;
  }
}

/** @param {string} value */
function escapeHtml(value) {
  return String(value ?? '').replace(
    /[&<>"']/g,
    (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char] ?? char
  );
}

if (!customElements.get('wishlist-page')) {
  customElements.define('wishlist-page', WishlistPage);
}
